import { Link } from "react-router-dom";
import { Building2, CheckCircle2, ClipboardList, FileText, Landmark, LifeBuoy, UserRound, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, pretty, todayISO, TASK_STATUS } from "@/lib/api";

const daysTo = (d) => Math.round((new Date(d) - new Date(todayISO())) / 86400000);
const left = (d) => { const n = daysTo(d); return n < 0 ? `${-n}d overdue` : n === 0 ? "Due today" : `${n} days left`; };
const tone = (d) => (daysTo(d) < 0 ? "bg-red-100 text-red-700" : daysTo(d) <= 7 ? "bg-accent/40 text-black" : "bg-primary/15 text-black");

const CAT_ICON = { tax: FileText, payroll: Users, social_security: Users, licensing: Landmark, corporate: Building2 };
const barColor = (c) => (c.overdue ? "bg-red-500" : c.ok === c.total ? "bg-emerald-500" : c.ok / c.total >= 0.5 ? "bg-[#00A9D7]" : "bg-[#FFD000]");
const shortDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(d.slice(0, 4) !== todayISO().slice(0, 4) ? { year: "numeric" } : {}) });
const chip = (n) => (n < 0 ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300" : n <= 7 ? "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300" : "bg-[#00A9D7]/15 text-[#006f8f] dark:text-sky-300");
const dot = (n) => (n < 0 ? "bg-red-500" : n <= 7 ? "bg-amber-500" : "bg-[#00A9D7]");
const Title = ({ children }) => <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{children}</h3>;

function Trend({ points }) {
  const pts = points.filter((p) => p.pct !== null);
  if (pts.length < 2) return <div className="text-right text-[11px] text-white/60" data-testid="health-trend-empty">Trend appears once there are at least two months of task history.</div>;
  const W = 240, H = 64, step = W / (points.length - 1);
  const xy = points.map((p, i) => p.pct === null ? null : [i * step, H - 6 - (p.pct / 100) * (H - 12)]);
  const path = xy.filter(Boolean).map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const mon = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { month: "short" });
  return (
    <div data-testid="health-trend" className="w-full max-w-[260px]">
      <div className="mb-1 text-right text-[11px] uppercase tracking-wider text-white/60">Last {points.length} months</div>
      <svg viewBox={`-4 0 ${W + 8} ${H}`} className="h-16 w-full overflow-visible">
        <path d={path} fill="none" stroke="#FFD000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {xy.map((p, i) => p && <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={i === points.length - 1 ? "#FFD000" : "#ffffff"}><title>{`${points[i].month}: ${points[i].pct}% (${points[i].tasks} tasks)`}</title></circle>)}
      </svg>
      <div className="mt-1 flex justify-between gap-1 text-center text-[10px] leading-tight text-white/60">{points.map((p) => <span key={p.date} data-testid={`health-trend-${p.month}`}>{mon(p.date)}<span className="block font-semibold text-white/85">{p.pct !== null ? `${p.pct}%` : "–"}</span></span>)}</div>
    </div>
  );
}

export function HubHealth({ h }) {
  const personal = h.scope === "personal";
  const due = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const stats = [["Need you", h.need_you, "#attention", "health-need"], ["Due in 30 days", h.due_30, `/tasks?window=upcoming&date_to=${due}`, "health-due30"], ["Overdue", h.overdue, "/tasks?overdue=true", "health-overdue"]];
  return (
    <section data-testid="health-card" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#05364b] via-[#045273] to-[#0089b3] p-6 text-white shadow-lg sm:p-8">
      <div className="pointer-events-none absolute -bottom-28 -right-10 h-72 w-72 rounded-full bg-[#FFD000]/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-[#00A9D7]/30 blur-3xl" />
      <div className="relative">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
        <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
          {personal ? <><span className="font-display text-7xl font-extrabold leading-none sm:text-8xl" data-testid="health-open-tasks">{h.open_tasks}</span><span className="pb-3 text-sm text-white/85">Your open tasks</span></>
            : h.pct === null ? <span className="font-display text-3xl font-bold" data-testid="health-pct">Not assessed yet</span>
            : <><span className="font-display text-7xl font-extrabold leading-none sm:text-8xl" data-testid="health-pct">{h.pct}</span><span className="pb-2 text-3xl text-white/80">%</span><span className="pb-3 text-sm text-white/85">Compliance health</span></>}
          {!personal && h.pct === null && <span className="pb-1 text-sm text-white/85">Compliance health</span>}
        </div>
        <p className="mt-2 max-w-xl text-[11px] text-white/60" data-testid="health-rule">{personal ? "Showing only the tasks you are responsible for. The company-wide compliance score is shown to users who can see all of the company's obligations." : `${h.rule} (${h.applicable_tasks} tasks).`}</p>
        </div>
        {!personal && h.trend && <Trend points={h.trend} />}
        </div>
        {personal ? null : h.categories.length ? <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">{h.categories.map((c) => (
          <div key={c.category} data-testid={`health-cat-${c.category}`}>
            <div className="h-2 overflow-hidden rounded-full bg-white/20">{c.total > 0 && <div className={`h-full rounded-full ${barColor(c)}`} style={{ width: `${(c.ok / c.total) * 100}%` }} />}</div>
            <div className="mt-2 flex justify-between gap-2 text-xs"><span className="truncate text-white/90">{pretty(c.category)}</span><span className="shrink-0 text-white/60">{c.total ? `${c.ok}/${c.total}` : "Not assessed"}</span></div>
          </div>
        ))}</div> : <p className="mt-6 text-sm text-white/70" data-testid="health-no-categories">No obligations have been marked applicable for this company yet.</p>}
        <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
          {stats.map(([l, v, to, id]) => {
            const body = <><div className="font-display text-3xl font-extrabold" data-testid={id}>{v}</div><div className="text-sm text-white/80">{l}</div></>;
            return to.startsWith("#") ? <a key={id} href={to} className="transition-opacity hover:opacity-80" data-testid={`${id}-link`}>{body}</a> : <Link key={id} to={to} className="transition-opacity hover:opacity-80" data-testid={`${id}-link`}>{body}</Link>;
          })}
        </div>
      </div>
    </section>
  );
}

export function HubAttention({ items, total, onOpen }) {
  return (
    <section id="attention" data-testid="attention-list" className="scroll-mt-6 rounded-2xl border bg-card p-6 shadow-sm">
      <Title>Needs your attention</Title>
      {!items.length ? (
        <div className="py-10 text-center" data-testid="attention-empty"><CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" /><p className="mt-2 font-semibold">You're all caught up</p><p className="text-sm text-muted-foreground">Nothing needs your action right now.</p></div>
      ) : <div className="divide-y">{items.map((t) => {
        const I = CAT_ICON[t.category] || ClipboardList;
        return (
          <div key={t.id} className="flex items-center gap-4 py-4" data-testid={`attention-${t.id}`}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#00A9D7]/10 text-[#0083a8] dark:text-sky-300"><I className="h-5 w-5" /></span>
            <button onClick={() => onOpen(t.id)} className="min-w-0 flex-1 text-left">
              <div className="truncate font-bold">{t.title}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
                <span className={daysTo(t.due_date) < 0 ? "font-medium text-red-600 dark:text-red-400" : ""}>{left(t.due_date)}</span><span>·</span><span>{t.next_action}</span>
                {t.assignee_name && <><span>·</span><UserRound className="h-3.5 w-3.5" /><span>{t.assignee_name}</span></>}
              </div>
            </button>
            <button onClick={() => onOpen(t.id)} data-testid={`attention-action-${t.id}`} className="shrink-0 rounded-lg bg-[#FFD000] px-4 py-2 text-sm font-bold text-black shadow-sm transition-transform hover:-translate-y-0.5">{t.action}</button>
          </div>
        );
      })}</div>}
      {total > items.length && <Link to="/tasks" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline" data-testid="attention-view-all">Showing {items.length} of {total} · view all in Compliances</Link>}
    </section>
  );
}

export function HubComingUp({ items, personal, onOpen }) {
  const seen = new Set();
  const list = items.filter((t) => { const k = `${t.title}|${t.due_date}`; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 6);
  return (
    <section className="rounded-2xl border bg-card p-6 shadow-sm" data-testid="coming-up">
      <Title>{personal ? "Coming up · your deadlines" : "Coming up"}</Title>
      {!list.length ? <p className="py-8 text-center text-sm text-muted-foreground" data-testid="coming-up-empty">{personal ? "No upcoming deadlines on your tasks." : "No upcoming deadlines."}</p> : (
        <ol className="relative ml-1.5 mt-4 border-l border-border">{list.map((t) => {
          const n = daysTo(t.due_date);
          return (
            <li key={t.id} className="relative pb-6 pl-6 last:pb-1" data-testid={`coming-${t.id}`}>
              <span className={`absolute -left-[4.5px] top-1.5 h-2 w-2 rounded-full ${dot(n)}`} />
              <div className="text-sm text-muted-foreground">{shortDate(t.due_date)}</div>
              <button onClick={() => onOpen(t.id)} className="mt-0.5 text-left font-bold hover:underline">{t.title}</button>
              <div className="mt-1.5 flex flex-wrap items-center gap-2"><span className={`rounded-md px-2 py-0.5 text-xs font-bold ${chip(n)}`}>{left(t.due_date)}</span><span className="text-[11px] text-muted-foreground">{TASK_STATUS[t.status]?.label}</span></div>
            </li>
          );
        })}</ol>
      )}
    </section>
  );
}

export function Greeting({ subtitle, company }) {
  const { user } = useAuth();
  const h = new Date().getHours();
  const g = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="mb-6 flex flex-col justify-between gap-2 sm:flex-row sm:items-end" data-testid="dashboard-greeting">
      <div>
        <h1 className="font-heading text-3xl font-extrabold sm:text-4xl">{g}, {user.name.split(" ")[0]}.</h1>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="greeting-subtitle">{subtitle}</p>
      </div>
      {company && <span className="rounded-full border bg-card px-4 py-2 text-sm font-semibold" data-testid="greeting-company">{company}</span>}
    </div>
  );
}

export function HealthCard({ h }) {
  return (
    <div className="va-hero rounded-[18px] border bg-card p-6" data-testid="health-card">
      <div className="flex items-end gap-3">
        {h.pct === null ? <span className="font-heading text-2xl font-bold" data-testid="health-pct">No applicable tasks yet</span>
          : <><span className="font-heading text-6xl font-extrabold leading-none" data-testid="health-pct">{h.pct}</span><span className="pb-1 text-xl">%</span></>}
        <span className="pb-1 text-sm text-muted-foreground">Compliance health</span>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground" data-testid="health-rule">{h.rule} ({h.applicable_tasks} tasks).</p>
      {h.categories.length > 0 && <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">{h.categories.map((c) => (
        <div key={c.category} data-testid={`health-cat-${c.category}`}>
          <div className="flex h-2 overflow-hidden rounded-full bg-muted"><div className={c.overdue ? "bg-red-600" : "bg-primary"} style={{ width: `${(c.ok / c.total) * 100}%` }} /></div>
          <div className="mt-1 text-xs">{pretty(c.category)} <span className="text-muted-foreground">{c.ok}/{c.total}</span></div>
        </div>
      ))}</div>}
      <div className="mt-5 flex gap-8">
        {[["Need you", h.need_you, "health-need"], ["Due in 30 days", h.due_30, "health-due30"], ["Overdue", h.overdue, "health-overdue"]].map(([l, v, id]) => (
          <div key={id}><div className={`font-heading text-2xl font-extrabold ${id === "health-overdue" && v ? "text-red-600" : ""}`} data-testid={id}>{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
        ))}
      </div>
    </div>
  );
}

export function Attention({ items, onOpen, showCompany }) {
  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="attention-list">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Needs your attention</h3>
      {!items.length ? <p className="text-sm text-muted-foreground" data-testid="attention-empty">Nothing needs your action right now.</p> : items.map((t) => (
        <div key={t.id} className="flex items-center gap-3 border-b py-3 last:border-0" data-testid={`attention-${t.id}`}>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{t.title}</div>
            <div className="text-xs text-muted-foreground">{left(t.due_date)} · {t.next_action}{t.assignee_name ? ` · ${t.assignee_name}` : ""}{showCompany ? ` · ${t.company_name}` : ""}</div>
          </div>
          <Button size="sm" className="rounded-full" onClick={() => onOpen(t.id)} data-testid={`attention-action-${t.id}`}>{t.action}</Button>
        </div>
      ))}
    </div>
  );
}

export function ComingUp({ items, exclude, onOpen }) {
  const list = items.filter((t) => !exclude.has(t.id)).slice(0, 5);
  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="coming-up">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Coming up</h3>
      {!list.length ? <p className="text-sm text-muted-foreground">No other upcoming deadlines.</p> : <ol className="space-y-4 border-l-2 border-primary/40 pl-4">{list.map((t) => (
        <li key={t.id} className="relative" data-testid={`coming-${t.id}`}>
          <span className={`absolute -left-[23px] top-1.5 h-3 w-3 rounded-full border-2 border-white ${daysTo(t.due_date) < 0 ? "bg-red-600" : "bg-primary"}`} />
          <div className="text-xs text-muted-foreground">{fmtDate(t.due_date)}</div>
          <button onClick={() => onOpen(t.id)} className="text-left font-semibold hover:underline">{t.title}</button>
          <div className="mt-1 flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(t.due_date)}`}>{left(t.due_date)}</span><StatusBadge status={t.status} /></div>
        </li>
      ))}</ol>}
    </div>
  );
}

export function RequestServiceCard({ consultant }) {
  return (
    <div className="flex flex-col justify-between gap-3 rounded-2xl bg-black dark:border dark:border-white/10 dark:bg-[#0b2a38] p-6 text-white sm:flex-row sm:items-center" data-testid="request-service-card">
      <div><p className="text-xs uppercase tracking-wider text-white/60">Your consultant</p>
        <p className="font-heading text-lg font-bold" data-testid="consultant-name">{consultant ? consultant.name : "V.A Business Consultants"}</p>
        {consultant?.email && <p className="text-xs text-white/70">{consultant.email}</p>}</div>
      <Link to="/requests/new" data-testid="request-service-btn"><Button className="rounded-full"><LifeBuoy className="mr-2 h-4 w-4" />Request a service</Button></Link>
    </div>
  );
}
